/**
 * Whitelist Markdown для экрана «Документация» staff SPA.
 * Группы: `user` — пользовательский гайд `docs/user/`, `dev` — материалы разработки и QA `docs/release/`.
 * Новый файл: добавить `import …?raw`, пункт в `DOCS_RELEASE_PAGES` и `sourcePath` (путь от корня репозитория).
 */
import userAccessRbac from '../../../../../docs/user/ACCESS_RBAC.md?raw';
import userMainAdminChange from '../../../../../docs/user/MAIN_ADMIN_CHANGE.md?raw';
import userAssistant from '../../../../../docs/user/ASSISTANT.md?raw';
import userClientPortal from '../../../../../docs/user/CLIENT_PORTAL.md?raw';
import userClients from '../../../../../docs/user/CLIENTS.md?raw';
import userEquipment from '../../../../../docs/user/EQUIPMENT.md?raw';
import userSalesFunnel from '../../../../../docs/user/SALES_FUNNEL.md?raw';
import userFaq from '../../../../../docs/user/FAQ.md?raw';
import userPrices from '../../../../../docs/user/PRICES.md?raw';
import userStart from '../../../../../docs/user/README.md?raw';
import userScheduleBookings from '../../../../../docs/user/SCHEDULE_BOOKINGS.md?raw';
import userTrainerApp from '../../../../../docs/user/TRAINER_APP.md?raw';
import userServices from '../../../../../docs/user/SERVICES.md?raw';
import userStartLogin from '../../../../../docs/user/START.md?raw';
import userSubscriptionsPayments from '../../../../../docs/user/SUBSCRIPTIONS_PAYMENTS.md?raw';
import userSuperadmin from '../../../../../docs/user/SUPERADMIN.md?raw';
import userTasks from '../../../../../docs/user/TASKS.md?raw';
import userWidgetMobileQr from '../../../../../docs/user/WIDGET_MOBILE_QR.md?raw';
import envAndAccess from '../../../../../docs/release/test/ENV_AND_ACCESS.md?raw';
import testIndex from '../../../../../docs/release/test/INDEX.md?raw';
import qaOnepage from '../../../../../docs/release/test/QA_RUN_REGULATION_ONEPAGE.md?raw';
import releaseReadme from '../../../../../docs/release/README.md?raw';

export type DocsReleaseGroup = 'user' | 'dev';

export type DocsReleaseEntry = {
  id: string;
  title: string;
  group: DocsReleaseGroup;
  /** Путь файла от корня репозитория — для резолва относительных ссылок в SPA. */
  sourcePath: string;
  markdown: string;
};

export type DocsReleaseGroupView = {
  id: DocsReleaseGroup;
  title: string;
  pages: DocsReleaseEntry[];
};

export const DOCS_RELEASE_GROUPS: readonly { id: DocsReleaseGroup; title: string }[] = [
  { id: 'user', title: 'Пользователю' },
  { id: 'dev', title: 'Разработчику и QA' }
];

export const DOCS_RELEASE_PAGES: readonly DocsReleaseEntry[] = [
  { id: 'user-start', title: 'Начало работы', group: 'user', sourcePath: 'docs/user/README.md', markdown: userStart },
  { id: 'user-login', title: 'Вход и доступ', group: 'user', sourcePath: 'docs/user/START.md', markdown: userStartLogin },
  {
    id: 'user-schedule',
    title: 'Расписание и записи',
    group: 'user',
    sourcePath: 'docs/user/SCHEDULE_BOOKINGS.md',
    markdown: userScheduleBookings
  },
  {
    id: 'user-trainer-app',
    title: 'Приложение тренера',
    group: 'user',
    sourcePath: 'docs/user/TRAINER_APP.md',
    markdown: userTrainerApp
  },
  { id: 'user-clients', title: 'Клиенты', group: 'user', sourcePath: 'docs/user/CLIENTS.md', markdown: userClients },
  { id: 'user-services', title: 'Услуги, типы и залы', group: 'user', sourcePath: 'docs/user/SERVICES.md', markdown: userServices },
  { id: 'user-prices', title: 'Как считается цена', group: 'user', sourcePath: 'docs/user/PRICES.md', markdown: userPrices },
  { id: 'user-equipment', title: 'Оборудование', group: 'user', sourcePath: 'docs/user/EQUIPMENT.md', markdown: userEquipment },
  { id: 'user-tasks', title: 'Задачи', group: 'user', sourcePath: 'docs/user/TASKS.md', markdown: userTasks },
  { id: 'user-sales-funnel', title: 'Воронка продаж', group: 'user', sourcePath: 'docs/user/SALES_FUNNEL.md', markdown: userSalesFunnel },
  {
    id: 'user-subscriptions',
    title: 'Абонементы и платежи',
    group: 'user',
    sourcePath: 'docs/user/SUBSCRIPTIONS_PAYMENTS.md',
    markdown: userSubscriptionsPayments
  },
  {
    id: 'user-access',
    title: 'Сотрудники и права',
    group: 'user',
    sourcePath: 'docs/user/ACCESS_RBAC.md',
    markdown: userAccessRbac
  },
  {
    id: 'user-main-admin-change',
    title: 'Как сменить главного администратора',
    group: 'user',
    sourcePath: 'docs/user/MAIN_ADMIN_CHANGE.md',
    markdown: userMainAdminChange
  },
  {
    id: 'user-widget',
    title: 'Виджет, приложение, QR',
    group: 'user',
    sourcePath: 'docs/user/WIDGET_MOBILE_QR.md',
    markdown: userWidgetMobileQr
  },
  { id: 'user-superadmin', title: 'Платформа (суперадмин)', group: 'user', sourcePath: 'docs/user/SUPERADMIN.md', markdown: userSuperadmin },
  { id: 'user-assistant', title: 'Помощник: действия с подтверждением', group: 'user', sourcePath: 'docs/user/ASSISTANT.md', markdown: userAssistant },
  {
    id: 'user-client-portal',
    title: 'Клиентский портал',
    group: 'user',
    sourcePath: 'docs/user/CLIENT_PORTAL.md',
    markdown: userClientPortal
  },
  { id: 'user-faq', title: 'Частые вопросы', group: 'user', sourcePath: 'docs/user/FAQ.md', markdown: userFaq },
  { id: 'release-readme', title: 'Релизный набор (обзор)', group: 'dev', sourcePath: 'docs/release/README.md', markdown: releaseReadme },
  { id: 'test-index', title: 'Test manual — оглавление', group: 'dev', sourcePath: 'docs/release/test/INDEX.md', markdown: testIndex },
  {
    id: 'qa-onepage',
    title: 'QA: регламент (one-page)',
    group: 'dev',
    sourcePath: 'docs/release/test/QA_RUN_REGULATION_ONEPAGE.md',
    markdown: qaOnepage
  },
  { id: 'env-access', title: 'Окружение и доступы', group: 'dev', sourcePath: 'docs/release/test/ENV_AND_ACCESS.md', markdown: envAndAccess }
];

/** Синхрон выбора документа в сайдбаре и на странице `/docs` через `?doc=`. */
export function defaultStaffDocsReleaseDocId(): string {
  return DOCS_RELEASE_PAGES[0]?.id ?? '';
}

export function normalizeStaffDocsReleaseDocParam(raw: string | null): string {
  if (!raw) return defaultStaffDocsReleaseDocId();
  const hit = DOCS_RELEASE_PAGES.find((p) => p.id === raw);
  return hit?.id ?? defaultStaffDocsReleaseDocId();
}

/** Страницы, сгруппированные для сайдбара; пустые группы отбрасываются. */
export function groupedStaffDocsReleasePages(): DocsReleaseGroupView[] {
  return DOCS_RELEASE_GROUPS.map((group) => ({
    ...group,
    pages: DOCS_RELEASE_PAGES.filter((p) => p.group === group.id)
  })).filter((group) => group.pages.length > 0);
}

export type ResolvedDocsReleaseLink = {
  docId: string;
  anchor: string | null;
};

function lookupBySourcePath(sourcePath: string): DocsReleaseEntry | undefined {
  return DOCS_RELEASE_PAGES.find((p) => p.sourcePath === sourcePath);
}

const ABSOLUTE_HREF = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

/**
 * Резолв ссылки внутри документов `/docs`:
 * относительный путь (`./PRICES.md`, `../user/START.md`) → id документа и якорь.
 * Возвращает null для внешних ссылок, `mailto:` и т.п.
 */
export function resolveDocsReleaseLink(
  currentSourcePath: string,
  href: string
): ResolvedDocsReleaseLink | null {
  const raw = href.trim();
  if (!raw || raw.startsWith('#')) return null;
  if (ABSOLUTE_HREF.test(raw)) return null;
  let resolved: URL;
  try {
    resolved = new URL(raw, `https://docs.local/${currentSourcePath}`);
  } catch {
    return null;
  }
  const sourcePath = decodeURIComponent(resolved.pathname.replace(/^\//, ''));
  const hit = lookupBySourcePath(sourcePath);
  if (!hit) return null;
  const anchor = resolved.hash.length > 1 ? decodeURIComponent(resolved.hash.slice(1)) : null;
  return { docId: hit.id, anchor };
}
